// src/pages/Signup.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
} from "@mui/icons-material";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { postData } from "../services/request";
import logo from "../assets/logo/logo3.png";
import countryData from "../data/countryData";

const labelSx = {
  display: "block",
  color: "#52057b",
  fontWeight: 600,
  fontSize: "0.9rem",
  mb: 1,
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0px 4px 12px rgba(82, 5, 123, 0.1)",
    },
    "&.Mui-focused": {
      boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
      borderColor: "#52057b",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "& .MuiInputBase-input": {
    py: 1.8,
  },
};

const Signup = () => {
  const navigate = useNavigate();

  // Form States
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    countryData[2],
  );
  const [selectedCountry, setSelectedCountry] = useState("Nigeria");
  const [userType, setUserType] = useState("groupUser");
  const [password, setPassword] = useState("");
  const [retryPassword, setRetryPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Validation states
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [retryPasswordTouched, setRetryPasswordTouched] = useState(false);

  // Validation functions
  const isPasswordValid = password.length >= 8;
  const isPasswordsMatch = password === retryPassword && password.length > 0;

  const getPasswordError = () => {
    if (!passwordTouched) return "";
    if (password.length === 0) return "Password is required";
    if (!isPasswordValid) return "Password must be at least 8 characters";
    return "";
  };

  const getRetryPasswordError = () => {
    if (!retryPasswordTouched) return "";
    if (retryPassword.length === 0) return "Please confirm your password";
    if (!isPasswordsMatch) return "Passwords do not match";
    return "";
  };

  // Button logic
  const isButtonEnabled = agreePrivacy && agreeTerms && isPasswordValid;

  const handleSignup = async () => {
    // Validations with toast
    if (!fullName || !email || !phone || !password) {
      toast.error("Please fill all fields");
      return;
    }
    if (phone.length < 10 || phone.length > 11) {
      toast.error("Phone number must be between 10 and 11 digits");
      return;
    }
    if (!isPasswordValid) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!isPasswordsMatch) {
      toast.error("Passwords do not match!");
      return;
    }
    if (!isButtonEnabled) {
      toast.error("Please accept Terms & Privacy Policy");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        full_name: fullName,
        email: email,
        phone: `${selectedCountryCode.code}${phone}`,
        password: password,
        userType: userType,
        country: selectedCountry,
        countryCode: selectedCountryCode.code,
      };
      const response = await postData("/api/auth/signup", payload);
      if (response.status === true) {
        if (Array.isArray(response.notifications)) {
          localStorage.setItem(
            "notifications",
            JSON.stringify(response.notifications),
          );
        }
        toast.success("Signup Successful!");
        setTimeout(() => navigate("/verify-otp", { state: { email } }), 1500);
      } else {
        toast.error(response.message || "Signup failed. Please try again.");
      }
    } catch (error) {
      toast.error("Network Error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)",
        py: 3,
      }}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: "12px",
            background: "#fff",
            padding: "16px 20px",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
          },
          success: {
            iconTheme: {
              primary: "#52057b",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#d32f2f",
              secondary: "#fff",
            },
          },
        }}
      />

      <Paper
        elevation={0}
        sx={{
          width: { xs: "90%", sm: 550 },
          p: { xs: 3, sm: 5 },
          borderRadius: "24px",
          background: "#fff",
          boxShadow: "0px 20px 60px rgba(82, 5, 123, 0.15)",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <img src={logo} alt="Dajoo" style={{ width: "120px" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ color: "#52057b", fontWeight: 800, mb: 1 }}
          >
            Create an Account
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", fontSize: "0.95rem" }}
          >
            Let get you started, this will only take few minutes
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form">
          {/* Full Name */}
          <Typography sx={labelSx}>Full Name</Typography>
          <TextField
            placeholder="e.g John Smith"
            fullWidth
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Email */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Email</Typography>
          <TextField
            placeholder="example@gmail.com"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Phone Number with Flag + Code */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Phone Number</Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Autocomplete
              options={countryData}
              getOptionLabel={(option) => option.code}
              value={selectedCountryCode}
              onChange={(e, newVal) => setSelectedCountryCode(newVal)}
              disableClearable
              sx={{
                width: "140px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                  "&.Mui-focused": {
                    boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <ReactCountryFlag
                    countryCode={option.iso}
                    svg
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                      borderRadius: "2px",
                    }}
                  />
                  <span>{option.code}</span>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  sx={{ "& .MuiInputBase-input": { py: 1.8 } }}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
            <TextField
              placeholder="Phone Number"
              fullWidth
              value={phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                setPhone(value);
              }}
              inputProps={{
                inputMode: "numeric",
                maxLength: 11,
              }}
              sx={inputSx}
            />

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.75,
                ml: 0.5,
                color: phone.length > 11 ? "#d32f2f" : "#666",
                fontSize: "0.72rem",
              }}
            >
              {phone.length}/11
            </Typography>
          </Box>
          {/* Country */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Country</Typography>
          <Autocomplete
            options={countryData}
            getOptionLabel={(option) => option.name}
            value={countryData.find((c) => c.name === selectedCountry) || null}
            onChange={(e, newVal) => {
              setSelectedCountry(newVal ? newVal.name : "Nigeria");
              setSelectedCountryCode(newVal ? newVal : countryData[2]);
            }}
            disableClearable
            fullWidth
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <ReactCountryFlag
                  countryCode={option.iso}
                  svg
                  style={{
                    width: "1.2em",
                    height: "1.2em",
                    borderRadius: "2px",
                  }}
                />
                <span>{option.name}</span>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root": {
                    ...inputSx["& .MuiOutlinedInput-root"],
                    borderRadius: "12px",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReactCountryFlag
                        countryCode={
                          countryData.find((c) => c.name === selectedCountry)
                            ?.iso
                        }
                        svg
                        style={{
                          width: "1.2em",
                          height: "1.2em",
                          borderRadius: "2px",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Category */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Category</Typography>
          <RadioGroup
            row
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            sx={{ mt: 1, mb: 1 }}
          >
            <FormControlLabel
              value="groupAdmin"
              control={
                <Radio
                  sx={{
                    color: "#52057b",
                    "&.Mui-checked": { color: "#52057b" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, color: "#333" }}>
                  Group Admin
                </Typography>
              }
              sx={{ mr: 5 }}
            />
            <FormControlLabel
              value="groupUser"
              control={
                <Radio
                  sx={{
                    color: "#52057b",
                    "&.Mui-checked": { color: "#52057b" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, color: "#333" }}>
                  Member
                </Typography>
              }
            />
          </RadioGroup>

          {/* Password */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Password</Typography>
          <TextField
            placeholder="**********"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setPasswordTouched(true)}
            error={passwordTouched && !isPasswordValid}
            helperText={passwordTouched && getPasswordError()}
            FormHelperTextProps={{
              sx: {
                color: "#d32f2f !important",
                fontWeight: 500,
                fontSize: "0.75rem",
                ml: 0,
              },
            }}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "#52057b" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Password Strength Indicator (Optional) */}
          {password.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isPasswordValid ? "#2e7d32" : "#d32f2f",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {isPasswordValid ? "✅" : "❌"}
                {isPasswordValid
                  ? "Password is strong"
                  : `Password must be at least 8 characters (${password.length}/8)`}
              </Typography>
            </Box>
          )}
          {/* Retry Password */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Confirm Password</Typography>
          <TextField
            placeholder="**********"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={retryPassword}
            onChange={(e) => setRetryPassword(e.target.value)}
            onBlur={() => setRetryPasswordTouched(true)}
            error={retryPasswordTouched && !isPasswordsMatch}
            helperText={retryPasswordTouched && getRetryPasswordError()}
            FormHelperTextProps={{
              sx: {
                color: "#d32f2f !important",
                fontWeight: 500,
                fontSize: "0.75rem",
                ml: 0,
              },
            }}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "#52057b" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Checkboxes */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeMarketing}
                  onChange={(e) => setAgreeMarketing(e.target.checked)}
                  sx={{
                    color: "#52057b",
                    "&.Mui-checked": { color: "#52057b" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#555" }}>
                  I agree to receive marketing communications (optional)
                </Typography>
              }
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  sx={{
                    color: "#52057b",
                    "&.Mui-checked": { color: "#52057b" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#555" }}>
                  I agree to the{" "}
                  <span
                    style={{
                      color: "#52057b",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Privacy Policy
                  </span>
                </Typography>
              }
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  sx={{
                    color: "#52057b",
                    "&.Mui-checked": { color: "#52057b" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#555" }}>
                  I agree to the{" "}
                  <span
                    style={{
                      color: "#52057b",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Terms & Conditions
                  </span>
                </Typography>
              }
            />
          </Box>

          {/* Continue Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSignup}
            disabled={!isButtonEnabled || loading}
            sx={{
              mt: 2,
              bgcolor: "#52057b",
              color: "#fff",
              py: 2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "1.1rem",
              textTransform: "none",
              boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
              "&:hover": {
                bgcolor: "#3e055e",
                boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
                transform: "translateY(-2px)",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Creating Account..." : "Continue"}
          </Button>

          <Typography
            variant="body2"
            sx={{ textAlign: "center", mt: 3, color: "#666" }}
          >
            Already have an account?{" "}
            <a
              href="/"
              style={{
                color: "#52057b",
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              Login
            </a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Signup;
